<template>
  <div class="rich-text break-keep" data-testid="styled-rich-text" :class="theme">
    <template v-for="(segment, index) in segments" :key="segment.kind === 'youtube' ? `youtube-${segment.videoId}-${index}` : `html-${index}`">
      <!-- HTML チャンク: display:contents の入れ物で従来どおり v-html 描画（:deep スタイルは descendant 一致で継続適用） -->
      <div v-if="segment.kind === 'html'" class="rich-text__chunk" v-html="segment.html" />
      <!-- YouTube 埋め込み: facade に差し替え。タイトルは facade が videoId から自動取得する -->
      <YouTubeFacade v-else class="my-6" :video-id="segment.videoId" />
    </template>
  </div>
</template>

<script setup lang="ts">
import YouTubeFacade from '@/wabunka-src/common/features/Featured/components/YouTubeFacade/YouTubeFacade.vue';
import { segmentizeHtml } from '@/wabunka-src/common/features/Featured/core/youtubeRichText';
import { ThemeKey } from '@/wabunka-src/common/features/Featured/injectionKeys';

const props = defineProps<{
  html: string;
}>();

const theme = inject(ThemeKey, ref('light'));
const segments = computed(() => segmentizeHtml(props.html));
</script>

<style lang="scss" scoped>
.rich-text {
  // HTML チャンクの入れ物はボックスを生成せず、従来の v-html 直描画と同じレイアウトを保つ
  .rich-text__chunk {
    display: contents;
  }

  // 本文タイポグラフィは「チャンク内」に限定する。
  // .rich-text 直下に :deep(p) 等を当てると、子コンポーネント（YouTubeFacade）内の
  // 要素（タイトル <p> 等）まで貫通して facade のスタイルを上書きしてしまうため、
  // リッチテキスト本文（= .rich-text__chunk の中身）に対象を絞る。
  :deep(.rich-text__chunk) {
    h1 {
      @apply break-words px-1 pb-4 pt-8 text-2xl text-gold3 md:text-3xl;
    }

    h2 {
      @apply px-1 pb-2 pt-6 text-xl text-light-fg-emphasis;
    }

    p {
      @apply px-1 py-1 text-base text-light-fg-default md:text-[17px];
    }

    a {
      @apply cursor-pointer text-gold3 underline;
    }

    ul {
      @apply m-0 list-disc pl-7 pt-0;
    }

    ol {
      @apply m-0 list-decimal pl-7 pt-0;
    }

    ol ol {
      @apply list-[lower-alpha];
    }

    li {
      @apply mx-auto py-1 text-light-fg-emphasis;
    }

    hr {
      @apply border-t border-light-bd-muted py-4;
    }

    table {
      @apply w-full max-w-full table-fixed;
    }

    table th {
      @apply w-[120px] px-1 text-start align-top font-bold text-light-fg-emphasis md:w-[160px];
    }

    table tr:not(:first-child) {
      @apply border-t border-light-bd-muted;
    }

    figure {
      @apply py-4;
    }

    table td {
      @apply min-w-[120px] px-1 align-top text-light-fg-emphasis;
    }

    figure figcaption {
      @apply px-1 pb-4 pt-1 text-[12px] text-gray4 md:px-0 md:text-[14px];
    }

    // カスタムクラスのスタイル
    .sub-title {
      @apply text-xl text-light-fg-emphasis;
    }

    .sub-text {
      @apply block pt-4 align-middle text-sm text-gray4;
    }

    .chip {
      @apply bg-light-bg-inset px-2 py-1 text-sm text-light-fg-accent;
    }

    .drawer-trigger {
      @apply cursor-pointer;
    }

    // NOTE: マテリアルアイコンのスタイル. 利用する場合pagesファイルのuseHeadでICON_FONT_URLを読み込むこと.
    .material-symbols-sharp {
      @apply mx-1 align-middle text-gray4;
    }
  }

  &.dark {
    :deep(.rich-text__chunk) {
      h1,
      a {
        @apply text-dark-fg-accent;
      }

      h2,
      p,
      li,
      table th,
      table td,
      .sub-title {
        @apply text-dark-fg-default;
      }

      figure figcaption,
      .sub-text,
      .material-symbols-sharp {
        @apply text-dark-fg-muted;
      }

      .chip {
        @apply bg-dark-bg-inset text-dark-fg-accent;
      }

      table tr:not(:first-child),
      hr {
        @apply border-dark-bd-muted;
      }
    }
  }
}
</style>
